import { useEffect, useState } from "react";
import { CaretLeft, CaretRight, DownloadSimple, MapPin, User, Clock, PencilSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { mediaSrc } from "@/components/service/serviceApi";
import { MediaLabelEditor } from "@/components/dailylogs/DlEditors";

export const fmtET = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET" : "");
const dl = (u) => mediaSrc(`${u}&download=true`);
export const isVideo = (m) => (m?.mimetype || "").startsWith("video/");
export const isImage = (m) => (m?.mimetype || "").startsWith("image/");
const SRC = { manual: ["corrected by management", "text-emerald-600"], gps: ["device GPS metadata", "text-emerald-600"] };

function locTag(m) {
  if (SRC[m.label_source]) return SRC[m.label_source];
  if (!m.label_site) return ["unconfirmed", "text-amber-600"];
  return m.label_confidence === "high" ? ["inferred from chat/Timesheet", "text-[#a78bfa]"] : ["inferred · check", "text-[#a78bfa]"];
}

function Meta({ m }) {
  const [tag, cls] = locTag(m);
  return (
    <div className="text-xs text-slate-500 space-y-1 min-w-0" data-testid="dl-viewer-meta">
      <div className="text-sm font-semibold text-slate-900 break-all" data-testid="dl-viewer-name">{m.display_name || m.id}</div>
      <div className="text-slate-700 font-medium" data-testid="dl-viewer-task">{m.label_task || m.ai_short || "—"}{m.label_status ? <span className="ml-2 text-emerald-600 text-[10px] uppercase font-bold">{m.label_status.replace("_", " ")}</span> : null}</div>
      {m.ai_description && <div className="text-slate-400">Visual: {m.ai_description}</div>}
      {m.transcript && <div className="italic text-slate-600">"{m.transcript}"</div>}
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1" data-testid="dl-viewer-location"><MapPin size={12} />{m.label_site || "Site unconfirmed"}{m.label_unit ? ` · Unit ${m.label_unit}` : ""}
          <span className={`ml-1 text-[9px] uppercase font-bold ${cls}`}>{tag}</span></span>
        <span className="inline-flex items-center gap-1"><User size={12} />{m.employee || m.sender_name || "Unknown"}{m.label_crew ? ` · crew ${m.label_crew}` : ""}</span>
        <span className="inline-flex items-center gap-1"><Clock size={12} />{fmtET(m.sent_at)} (sent)</span>
        {m.gps && <span className="text-emerald-600">GPS (device) {m.gps[0].toFixed(5)}, {m.gps[1].toFixed(5)}</span>}
        {m.width && <span>{m.width}×{m.height}px</span>}
      </div>
    </div>
  );
}

function Stage({ m, orig }) {
  const src = mediaSrc(orig || !m.stamped_url ? m.original_url : m.stamped_url);
  if (isVideo(m)) return orig || !m.stamped_preview_url
    ? <video key={m.original_url} src={mediaSrc(m.original_url)} controls autoPlay className="max-h-[65vh] w-full" data-testid="dl-viewer-video" />
    : <img src={mediaSrc(m.stamped_preview_url)} alt="" className="max-h-[65vh] object-contain" data-testid="dl-viewer-video-preview" />;
  if (isImage(m)) return <img src={src} alt={m.label_task || ""} className="max-h-[65vh] object-contain" data-testid="dl-viewer-image" />;
  if (m.kind === "audio") return <audio src={mediaSrc(m.original_url)} controls className="w-3/4" data-testid="dl-viewer-audio" />;
  return <a href={dl(m.original_url)} className="text-white underline">Download file</a>;
}

export function DlMediaViewer({ items, index, onClose, onChanged }) {
  const [list, setList] = useState(items);
  const [i, setI] = useState(index);
  const [orig, setOrig] = useState(false);
  const [edit, setEdit] = useState(false);
  useEffect(() => { setList(items); setI(index); setOrig(false); }, [items, index]);
  const m = index == null ? null : list[i];
  const go = (d) => { setOrig(false); setEdit(false); setI((x) => (x + d + list.length) % list.length); };
  useEffect(() => {
    if (index == null) return undefined;
    const h = (e) => { if (e.target.tagName === "INPUT") return; if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });
  if (!m) return null;
  const replace = (nm) => { setList((l) => l.map((x) => (x.id === nm.id ? nm : x))); onChanged?.(); };
  const restamp = async () => { const { data } = await api.post(`/dailylogs/media/${m.id}/restamp`); replace(data); toast.success("Stamp regenerated"); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl p-4" data-testid="dl-media-viewer">
        <DialogTitle className="sr-only">Media viewer</DialogTitle>
        <div className="relative bg-black rounded-lg flex items-center justify-center min-h-[280px] max-h-[65vh] overflow-hidden"><Stage m={m} orig={orig} />
          {list.length > 1 && <>
            <button onClick={() => go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors" data-testid="dl-viewer-prev"><CaretLeft size={20} /></button>
            <button onClick={() => go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-2 transition-colors" data-testid="dl-viewer-next"><CaretRight size={20} /></button>
          </>}
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-3">
          <Meta m={m} />
          <div className="flex flex-wrap gap-2 shrink-0">
            {(m.stamped_url || m.stamped_preview_url) && <Button size="sm" variant="outline" onClick={() => setOrig((v) => !v)} data-testid="dl-viewer-toggle">{orig ? "Show stamped" : isVideo(m) ? "Play original" : "Show original"}</Button>}
            <Button size="sm" variant="outline" onClick={() => setEdit((v) => !v)} data-testid="dl-viewer-edit"><PencilSimple size={14} className="mr-1" />Correct</Button>
            <Button size="sm" variant="outline" onClick={restamp} data-testid="dl-viewer-restamp"><ArrowsClockwise size={14} className="mr-1" />Regenerate stamp</Button>
            {(m.stamped_url || m.stamped_preview_url) && <a href={dl(m.stamped_url || m.stamped_preview_url)} data-testid="dl-viewer-download-stamped"><Button size="sm"><DownloadSimple size={14} className="mr-1" />Stamped</Button></a>}
            <a href={dl(m.original_url)} data-testid="dl-viewer-download-original"><Button size="sm" variant="outline"><DownloadSimple size={14} className="mr-1" />Original</Button></a>
          </div>
        </div>
        {edit && <MediaLabelEditor key={m.id} m={m} onSaved={(nm) => { replace(nm); setEdit(false); }} />}
        <div className="text-[10px] text-slate-400 text-right">{i + 1} / {list.length}</div>
      </DialogContent>
    </Dialog>
  );
}

export function Thumb({ m, onClick, className = "" }) {
  return (
    <button onClick={onClick} className={`relative group overflow-hidden rounded bg-slate-100 ${className}`} title={m.display_name || ""} data-testid={`dl-thumb-${m.id}`}>
      {m.thumb_url ? <img src={mediaSrc(m.thumb_url)} alt="" loading="lazy" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        : <div className="w-full h-full flex items-center justify-center text-[9px] text-slate-400">{m.kind}</div>}
      {isVideo(m) && <span className="absolute bottom-0.5 right-0.5 bg-black/70 text-white text-[8px] px-1 rounded">VID</span>}
    </button>
  );
}
